
import { useState, useEffect , useRef} from "react";
import { Link, useNavigate } from "react-router-dom";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { useDispatch, useSelector } from "react-redux";
import {
  getObjectionAction,
  deleteObjectionAction,
} from "../../../store/actions/ObjectionAction";

const Objection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let searchTimeout = null;
  const isFirstLoad = useRef(true);

  // ✅ REDUX STATE
  const objectionList = useSelector((state) => state.objection.list);
  const numRows = useSelector((state) => state.objection.num_rows || 0);

  const [loading, setLoading] = useState(false);
  const [searchQueries, setSearchQueries] = useState({
    objection_text: "",
    status: "",
  });

  const [sortField, setSortField] = useState("objection_text");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState("");

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  const rowsPerPage = 10;

  const fetchObjections = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      // ✅ Show loader only on first page load
      if (isFirstLoad.current) {
        setLoading(true);
      }
  
      await dispatch(
        getObjectionAction(
          pagination,
          pageid,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      );
    } catch (err) {
      console.error("Failed to fetch objections:", err);
      setError("Failed to load objections.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // ✅ stop loader forever
    }
  };
  
  

  useEffect(() => {
    fetchObjections();
  }, []);

  // ================= SEARCH =================
  const handleSearch = (e, field) => {
    const keyword = e.target.value;
  
    const clearedQueries = {
      objection_text: "",
      status: "",
    };
  
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);
  
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);
  
    // ✅ Debounce API call
    if (searchTimeout) clearTimeout(searchTimeout);
  
    searchTimeout = setTimeout(() => {
      fetchObjections(
        field === "status" ? "objection_status" : "objection_text",
        keyword,
        true,
        0,
        sortField,
        sortOrder
      );
    }, 400); // 400ms delay
  };
  

  // ================= SORT =================
  const handleSort = (field) => {
    const order =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0); // reset to first page on new sort

    fetchObjections(
      activeSearchField,
      activeSearchKeyword,
      true,
      0,
      field,
      order
    );
  };

  // ================= PAGINATION =================
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchObjections(
      activeSearchField,
      activeSearchKeyword,
      true,
      page,
      sortField,
      sortOrder
    );
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This objection will be deleted permanently!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (yes) => {
      if (yes) {
        try {
          setLoading(true);
          await dispatch(deleteObjectionAction(id));
          // Refresh the current page after deletion
          await fetchObjections(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete objection");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Server already returns paginated data (no client-side filtering)
  const paginatedData = objectionList;
  const totalPages = Math.ceil(numRows / rowsPerPage);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/add-objection" className="btn btn-primary btn-rounded">
            + Add Objection
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="text-danger mb-2">{error}</div>}
          {loading ? (
            <div className="text-center my-5">
              <Loader size="lg" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped mb-4">
                  <thead className="table-light">
                    <tr>
                      <th
                        onClick={() => handleSort("objection_text")}
                        style={{ cursor: "pointer" }}
                      >
                        Objection{" "}
                        {sortField === "objection_text" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.objection_text}
                          onChange={(e) => handleSearch(e, "objection_text")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Objection"
                        />
                      </th>

                      <th
                        onClick={() => handleSort("status")}
                        style={{ cursor: "pointer" }}
                      >
                        Status{" "}
                        {sortField === "status" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.status}
                          onChange={(e) => handleSearch(e, "status")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Status"
                        />
                      </th>

                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData?.length ? (
                      paginatedData.map((item) => (
                        <tr key={item.objection_id}>
                          <td>{item.objection_text}</td>
                          <td>
                            <span
                              className={`badge ${
                                item.is_active ? "bg-success" : "bg-danger"
                              }`}
                            >
                              {item.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex">
                              <span
                                className="edit me-4"
                                onClick={() =>
                                  navigate("/add-objection", {
                                    state: {
                                      item,
                                      returnTo: {
                                        currentPage,
                                        searchQueries,
                                        sortField,
                                        sortOrder,
                                      },
                                    },
                                  })
                                }
                                style={{ cursor: "pointer" }}
                                title="Edit Objection"
                              >
                                <i className="fa fa-pencil-alt text-success" />
                              </span>
                              <span
                                className="delete me-2"
                                onClick={() =>
                                  handleDelete(item.objection_id)
                                }
                                style={{ cursor: "pointer" }}
                                title="Delete Objection"
                              >
                                <i className="fa fa-trash text-danger" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">
                          No Objection Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div
                className="pagination-wrapper"
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "20px",
                  flexWrap: "wrap",
                  flexDirection: "column",
                }}
              >
                <div>
                  Showing{" "}
                  {paginatedData?.length === 0
                    ? 0
                    : currentPage * rowsPerPage + 1}{" "}
                  to{" "}
                  {Math.min(
                    (currentPage + 1) * rowsPerPage,
                    numRows
                  )}{" "}
                  of {numRows} entries
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={() =>
                      handlePageChange(Math.max(currentPage - 1, 0))
                    }
                    disabled={currentPage === 0}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor:
                        currentPage === 0 ? "not-allowed" : "pointer",
                      opacity: currentPage === 0 ? 0.5 : 1,
                    }}
                  >
                    &lt;
                  </button>

                  {(() => {
                    const pages = [];
                    const maxPagesToShow = 5;
                    const startPage = Math.max(
                      0,
                      Math.min(
                        currentPage - 2,
                        totalPages - maxPagesToShow
                      )
                    );
                    const endPage = Math.min(
                      startPage + maxPagesToShow,
                      totalPages
                    );

                    if (startPage > 0) {
                      pages.push(
                        <button
                          key={0}
                          onClick={() => handlePageChange(0)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          1
                        </button>
                      );
                      if (startPage > 1) {
                        pages.push(
                          <span
                            key="dots1"
                            style={{
                              padding: "6px 12px",
                              userSelect: "none",
                            }}
                          >
                            ...
                          </span>
                        );
                      }
                    }

                    for (let i = startPage; i < endPage; i++) {
                      const isActive = currentPage === i;
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: isActive ? "#007bff" : "#fff",
                            color: isActive ? "#fff" : "#000",
                            cursor: "pointer",
                          }}
                        >
                          {i + 1}
                        </button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span
                            key="dots2"
                            style={{
                              padding: "6px 12px",
                              userSelect: "none",
                            }}
                          >
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages - 1}
                          onClick={() => handlePageChange(totalPages - 1)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}

                  <button
                    onClick={() =>
                      handlePageChange(
                        Math.min(currentPage + 1, totalPages - 1)
                      )
                    }
                    disabled={currentPage === totalPages - 1}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor:
                        currentPage === totalPages - 1
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        currentPage === totalPages - 1 ? 0.5 : 1,
                    }}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Objection;
